import { Truck, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/shared/SEO';

const VisionPage = () => {
  return (
    <div data-testid="vision-page">
      <SEO 
        title="Our Vision | The Queen B Ecosystem"
        description="Queen B Logistics is building a comprehensive one-stop ecosystem for trucking and logistics professionals. Build. Protect. Grow."
        canonical="https://queenblogistics.com/vision"
        keywords="Queen B Logistics vision, logistics ecosystem, trucking services, one stop shop trucking"
      />
      {/* Page Header */}
      <section className="page-header relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="limited-badge mb-6">
            The Ecosystem
          </div>
          <h1 className="font-['Barlow_Condensed'] text-6xl md:text-7xl lg:text-8xl font-black text-white uppercase">
            Our<br />
            <span className="text-[#D4AF37]">Vision</span>
          </h1>
        </div>
        
        {/* Bee Watermark */}
        <div className="absolute -right-20 top-20 text-[300px] text-white/[0.02] font-['Barlow_Condensed'] font-black select-none pointer-events-none">
          &#9733;
        </div>
      </section>

      {/* Content */}
      <section className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {/* Gold Highlight Quote */}
          <div className="mb-16">
            <div className="h-1 w-16 bg-[#D4AF37] mb-8" />
            <blockquote className="font-['Barlow_Condensed'] text-3xl md:text-4xl lg:text-5xl font-bold text-[#D4AF37] leading-tight">
              To become the most comprehensive one-stop ecosystem for trucking and logistics 
              professionals—a single destination where carriers can Build, Protect, and Grow 
              every aspect of their business.
            </blockquote>
          </div>

          {/* Body Text */}
          <div className="space-y-6 text-white/70 text-lg leading-relaxed">
            <p>
              Queen B Logistics started with a simple belief: owner-operators deserve better. 
              Better support. Better guidance. Better opportunities. What began as premium dispatch 
              services has evolved into something bigger—a vision for a complete trucking and 
              logistics ecosystem.
            </p>
            
            <p>
              Through our <span className="text-[#D4AF37] font-semibold">Build • Protect • Grow</span> framework, 
              we've proven that structured support transforms businesses. Now we're expanding that 
              framework to create a true one-stop shop for everything a trucking professional needs 
              to launch, operate, and scale.
            </p>

            <p>
              Our vision is to eliminate the fragmentation that plagues this industry. Instead of 
              juggling multiple vendors, services, and relationships, carriers will have one trusted 
              partner who understands their business from day one through every stage of growth.
            </p>

            <p>
              We're not just building a company. We're building a movement. A community of 
              professional truckers who support each other, share knowledge, and rise together. 
              <span className="text-[#D4AF37]"> The hive is stronger when we work as one.</span>
            </p>
          </div>

          {/* The Ecosystem Vision */}
          <div className="mt-16 p-8 bg-[#121212] border border-[#D4AF37]/20">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-[#D4AF37]/10 flex items-center justify-center">
                <Truck className="w-7 h-7 text-[#D4AF37]" />
              </div>
              <div>
                <h3 className="font-['Barlow_Condensed'] text-2xl font-bold text-white">
                  The Queen B Ecosystem
                </h3>
                <p className="text-white/50 text-sm">One partner. Every solution.</p>
              </div>
            </div>
            
            <p className="text-white/60 mb-6 leading-relaxed">
              We're building toward a future where Queen B Logistics is the only partner a 
              trucking professional needs. From launching your authority to scaling your fleet, 
              from staying compliant to maximizing revenue—every service, every solution, under 
              one roof. One relationship. One standard of excellence.
            </p>
            
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="text-center p-4 bg-[#CD7F32]/10 border border-[#CD7F32]/20">
                <p className="font-['Barlow_Condensed'] text-xl font-bold text-[#CD7F32]">Build</p>
                <p className="text-white/40 text-xs mt-1">Launch</p>
              </div>
              <div className="text-center p-4 bg-[#C0C0C0]/10 border border-[#C0C0C0]/20">
                <p className="font-['Barlow_Condensed'] text-xl font-bold text-[#C0C0C0]">Protect</p>
                <p className="text-white/40 text-xs mt-1">Operate</p>
              </div>
              <div className="text-center p-4 bg-[#D4AF37]/10 border border-[#D4AF37]/20">
                <p className="font-['Barlow_Condensed'] text-xl font-bold text-[#D4AF37]">Grow</p>
                <p className="text-white/40 text-xs mt-1">Scale</p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 p-8 bg-gradient-to-r from-[#CD7F32]/10 via-[#C0C0C0]/10 to-[#D4AF37]/10 border border-white/10">
            <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-white mb-3">
              Join the Movement
            </h3>
            <p className="text-white/60 mb-6">
              Be part of something bigger. Whether you're launching your first authority or 
              scaling an established operation, the Queen B ecosystem is built for you.
            </p>
            <Link 
              to="/intake" 
              className="inline-flex items-center gap-2 text-[#D4AF37] font-bold hover:gap-3 transition-all"
            >
              Start Your Journey <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          {/* Bottom Quote */}
          <div className="mt-16 pt-12 border-t border-white/10">
            <p className="text-white/40 italic text-center">
              "Limited Access. Quality Over Quantity. One Ecosystem."
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VisionPage;
